import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AppDropdown from "../../ui/components/AppDropdown";
import logo from "../../assets/logo.png";
import ChatHistoryList from "../../pages/Home/components/ChatHistoryList";
import AppAlert from "../../ui/components/AppAlert";
import CreateClientModal from "./components/CreateClientModal";
import UploadKnowledgeBaseModal from "./components/UploadKBModal";
import CreateProjectModal from "./components/CreateProjectModal";
import CreateWorkroomModal from "./components/CreateWorkroomModal";
import HeaderProjectsPanel from "./components/HeaderProjectsPanel";
import WorkroomStrategyPanel from "../../features/project/components/WorkroomStrategyPanel";
import SidebarPanelIcon from "./components/SidebarPanelIcon";
import useHeaderLogic from "./hooks/useHeaderLogic";
import useHeaderProjects from "./hooks/useHeaderProjects";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
  homeGlassHero,
} from "../../pages/Home/constants/homeGlass";
import {
  DESKTOP_SHELL_HEIGHT,
  MOBILE_SHELL_HEIGHT,
  SAFE_AREA_BOTTOM,
  SAFE_AREA_TOP,
  SIDEBAR_COLLAPSED_WIDTH_PX,
  SIDEBAR_EXPANDED_WIDTH_PX,
  mobileNavMediaQuery,
} from "../layoutMetrics";
import { getProjectId } from "./utils/projectUtils";
import { useSidebar } from "../SidebarContext";
import Footer from "../Footer/Footer";
import {
  EDIT_WORKROOM_EVENT,
  CREATE_WORKROOM_EVENT,
  EDIT_PROJECT_EVENT,
  dispatchWorkroomUpdated,
  WORKROOM_STEP,
} from "../workroomEditEvents";
import { OPEN_KB_UPLOAD_EVENT } from "../kbUploadEvents";
import { CLIENT_STEP } from "./components/CreateClientModal/shared";
import { PROJECT_STEP } from "./components/CreateProjectModal/shared";

const HEADER_COLUMN_HEIGHT = `calc(${DESKTOP_SHELL_HEIGHT} - 68px)`;
const BRAND_MARK_LEFT_OFFSET = "-4.92px";
const BRAND_TITLE_TOP_OFFSET = "-22.49px";

export default function Header() {
  const {
    alertOpen,
    setAlertOpen,
    clientModalOpen,
    setClientModalOpen,
    projectModalOpen,
    setProjectModalOpen,
    kbUploading,
    kbUploadProgress,
    kbModalOpen,
    kbUploadModalKey,
    openClientKbModal,
    openProjectKbModal,
    closeKbModal,
    clientSwitchPromptOpen,
    setClientSwitchPromptOpen,
    handleContinueCurrentChat,
    handleStartNewClientChat,
    safeClient,
    handleClientChange,
    handleProjectClick,
    handleWorkroomClick,
    handleGoHome,
    activeProjectId,
    shouldShowChatHistory,
    clients,
    projects,
    archivedProjects,
    isGenerating,
    createClient: handleCreateClient,
    updateClient: handleUpdateClient,
    createProject,
    updateProject,
    handleDeleteProject,
    updateProjectIsArchived,
    refreshProjectFiles,
    loading,
    saveProgress,
    handleKbModalUpload,
    kbUploadForProject,
    projectCreatedAlertOpen,
    setProjectCreatedAlertOpen,
    projectCreatedAlertMessage,
  } = useHeaderLogic();

  const headerProjects = useHeaderProjects({
    projects,
    archivedProjects,
    activeProjectId,
    handleProjectClick,
    handleDeleteProject,
    updateProjectIsArchived,
    createProject,
    loading,
    isGenerating,
    refreshProjectFiles,
  });

  const isMobileNav = useMediaQuery(mobileNavMediaQuery);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [workroomModalOpen, setWorkroomModalOpen] = useState(false);
  const [workroomModalMode, setWorkroomModalMode] = useState("create");
  const [workroomModalProject, setWorkroomModalProject] = useState(null);
  const [workroomEditTarget, setWorkroomEditTarget] = useState(null);
  const [workroomDefaultStep, setWorkroomDefaultStep] = useState(null);
  const [workroomRefreshKey, setWorkroomRefreshKey] = useState(0);
  const [clientModalMode, setClientModalMode] = useState("create");
  const [clientEditTarget, setClientEditTarget] = useState(null);
  const [clientDefaultStep, setClientDefaultStep] = useState(null);
  const [projectModalMode, setProjectModalMode] = useState("create");
  const [projectEditTarget, setProjectEditTarget] = useState(null);
  const [projectDefaultStep, setProjectDefaultStep] = useState(null);
  const [isProjectsSectionOpen, setIsProjectsSectionOpen] = useState(false);
  const location = useLocation();
  const { isSidebarOpen, openSidebar, closeSidebar } = useSidebar();
  const isSidebarCollapsed = !isMobileNav && !isSidebarOpen;
  const isWorkroomRoute = location.pathname.startsWith("/project/");
  const workroomProjectId = isWorkroomRoute
    ? location.pathname.split("/project/")[1]?.split("/")[0] ?? null
    : null;
  const shouldFillSidebar = shouldShowChatHistory || isWorkroomRoute;
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  const handleExpandedBrandClick = () => {
    if (isGenerating) return;
    handleGoHome();
  };

  const handleCollapsedLogoClick = () => {
    if (!isGenerating) openSidebar();
  };

  const handleCollapsedBrandClick = () => {
    if (!isGenerating) handleGoHome();
  };

  const openCreateClientModal = () => {
    setClientModalMode("create");
    setClientEditTarget(null);
    setClientDefaultStep(null);
    setClientModalOpen(true);
  };

  const openEditClientModal = (client, defaultStep = null) => {
    setClientModalMode("edit");
    setClientEditTarget(client ?? null);
    setClientDefaultStep(
      typeof defaultStep === "number" ? defaultStep : null,
    );
    setClientModalOpen(true);
  };

  const closeClientModal = () => {
    setClientModalOpen(false);
    setClientModalMode("create");
    setClientEditTarget(null);
    setClientDefaultStep(null);
  };

  const openCreateProjectModal = () => {
    setProjectModalMode("create");
    setProjectEditTarget(null);
    setProjectDefaultStep(null);
    setProjectModalOpen(true);
  };

  const openEditProjectModal = (project, defaultStep = null) => {
    setProjectModalMode("edit");
    setProjectEditTarget(project ?? null);
    setProjectDefaultStep(
      typeof defaultStep === "number" ? defaultStep : null,
    );
    setProjectModalOpen(true);
  };

  const closeProjectModal = () => {
    setProjectModalOpen(false);
    setProjectModalMode("create");
    setProjectEditTarget(null);
    setProjectDefaultStep(null);
  };

  const openCreateWorkroom = (project, defaultStep = null) => {
    setWorkroomModalMode("create");
    setWorkroomEditTarget(null);
    setWorkroomDefaultStep(defaultStep);
    setWorkroomModalProject(project ?? null);
    setWorkroomModalOpen(true);
  };

  const openEditWorkroom = (workroom, step = null) => {
    const project =
      projects.find((entry) => String(getProjectId(entry)) === String(workroomProjectId)) ??
      null;
    setWorkroomModalMode("edit");
    setWorkroomEditTarget(workroom);
    setWorkroomDefaultStep(step);
    setWorkroomModalProject(project);
    setWorkroomModalOpen(true);
  };

  const closeCreateWorkroom = () => {
    setWorkroomModalOpen(false);
    setWorkroomModalMode("create");
    setWorkroomModalProject(null);
    setWorkroomEditTarget(null);
    setWorkroomDefaultStep(null);
  };

  const handleWorkroomSaved = () => {
    setWorkroomRefreshKey((key) => key + 1);
    dispatchWorkroomUpdated();
  };

  useEffect(() => {
    const onEditWorkroomRequest = (event) => {
      const { workroom, step } = event.detail ?? {};
      if (!workroom) return;
      openEditWorkroom(workroom, step ?? null);
    };

    window.addEventListener(EDIT_WORKROOM_EVENT, onEditWorkroomRequest);
    return () => {
      window.removeEventListener(EDIT_WORKROOM_EVENT, onEditWorkroomRequest);
    };
    // openEditWorkroom closes over projects + workroomProjectId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, workroomProjectId]);

  useEffect(() => {
    const onCreateWorkroomRequest = (event) => {
      const { step, projectId } = event.detail ?? {};
      const targetProjectId = projectId ?? workroomProjectId;
      if (targetProjectId == null) return;

      const project =
        projects.find(
          (entry) => String(getProjectId(entry)) === String(targetProjectId),
        ) ?? { projectId: targetProjectId };

      openCreateWorkroom(
        project,
        typeof step === "number" ? step : WORKROOM_STEP.CONCEPT,
      );
    };

    window.addEventListener(CREATE_WORKROOM_EVENT, onCreateWorkroomRequest);
    return () => {
      window.removeEventListener(
        CREATE_WORKROOM_EVENT,
        onCreateWorkroomRequest,
      );
    };
  }, [projects, workroomProjectId]);

  useEffect(() => {
    const onOpenKbUpload = (event) => {
      const projectId = event.detail?.projectId ?? null;
      if (projectId != null && String(projectId).trim() !== "") {
        const project =
          projects.find(
            (entry) => String(getProjectId(entry)) === String(projectId),
          ) ??
          archivedProjects.find(
            (entry) => String(getProjectId(entry)) === String(projectId),
          ) ??
          { projectId };
        openEditProjectModal(project, PROJECT_STEP.ASSETS);
        return;
      }

      if (!safeClient) return;
      openEditClientModal(safeClient, CLIENT_STEP.ASSETS);
    };

    window.addEventListener(OPEN_KB_UPLOAD_EVENT, onOpenKbUpload);
    return () => {
      window.removeEventListener(OPEN_KB_UPLOAD_EVENT, onOpenKbUpload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, archivedProjects, safeClient]);

  useEffect(() => {
    const onEditProjectRequest = (event) => {
      const { projectId, step } = event.detail ?? {};
      const targetProjectId = projectId ?? workroomProjectId;
      if (targetProjectId == null || String(targetProjectId).trim() === "") {
        return;
      }

      const project =
        projects.find(
          (entry) => String(getProjectId(entry)) === String(targetProjectId),
        ) ??
        archivedProjects.find(
          (entry) => String(getProjectId(entry)) === String(targetProjectId),
        ) ??
        { projectId: targetProjectId };

      openEditProjectModal(
        project,
        typeof step === "number" ? step : PROJECT_STEP.DETAILS,
      );
    };

    window.addEventListener(EDIT_PROJECT_EVENT, onEditProjectRequest);
    return () => {
      window.removeEventListener(EDIT_PROJECT_EVENT, onEditProjectRequest);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, archivedProjects, workroomProjectId]);

  const outerNavShellSx = {
    px: 2,
    pt: "max(4px, env(safe-area-inset-top, 0px))",
    width: SIDEBAR_EXPANDED_WIDTH_PX,
    boxSizing: "border-box",
    height: shouldFillSidebar ? HEADER_COLUMN_HEIGHT : "auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: shouldFillSidebar ? "hidden" : "visible",
  };

  const innerNavColumnSx = {
    // Fixed width: column must not shrink-wrap when content changes.
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 1.45,
    height: shouldFillSidebar ? "100%" : "auto",
    overflow: shouldFillSidebar ? "hidden" : "visible",
    minHeight: 0,
    pointerEvents: isGenerating ? "none" : "auto",
    opacity: isGenerating ? 0.7 : 1,
  };

  const navColumn = (
        <Box sx={innerNavColumnSx}>
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              mt: 0,
              flexDirection: "column",
              position: "relative",
              cursor: isGenerating ? "not-allowed" : "pointer",
            }}
            onClick={handleExpandedBrandClick}
          >
            <Tooltip
              title={isMobileNav ? "Close sidebar" : "Collapse sidebar"}
              placement="right"
            >
              <IconButton
                aria-label={isMobileNav ? "Close sidebar" : "Collapse sidebar"}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isMobileNav) {
                    setMobileDrawerOpen(false);
                  } else {
                    closeSidebar();
                  }
                }}
                disabled={isGenerating}
                sx={{
                  position: "absolute",
                  top: 6,
                  right: -5,
                  width: 32,
                  height: 32,
                  color: "rgba(255,255,255,0.78)",
                  borderRadius: "8px",
                  background: "transparent",
                  border: 0,
                  boxShadow: "none",
                  transition: "color 180ms ease, background 180ms ease",
                  "&:hover": {
                    background: "rgba(255,255,255,0.06)",
                    color: "#fff",
                  },
                }}
              >
                <SidebarPanelIcon sx={{ width: 22, height: 22 }} />
              </IconButton>
            </Tooltip>

            <Box
              component="img"
              src={logo}
              alt="logo"
              width={74}
              height="auto"
              sx={{
                display: "block",
                maxWidth: 74,
                height: "auto",
                ml: BRAND_MARK_LEFT_OFFSET,
              }}
            />
            <Typography
              sx={{
                mt: BRAND_TITLE_TOP_OFFSET,
                mb: 1,
                fontSize: 22,
                fontWeight: 500,
                lineHeight: 1.25,
                color: "#fff",
              }}
            >
              Performance
              <br />
              Branding Platform
            </Typography>
          </Box>

            <AppDropdown
              options={clients}
              value={safeClient}
              onChange={handleClientChange}
              onNewClient={openCreateClientModal}
              onEditClient={openEditClientModal}
              disabled={isGenerating}
              emphasized={!safeClient}
            />

          {safeClient && (
            <HeaderProjectsPanel
              {...headerProjects}
              activeProjectId={activeProjectId}
              openClientKbModal={openClientKbModal}
              openProjectKbModal={openProjectKbModal}
              handleProjectClick={handleProjectClick}
              handleWorkroomClick={handleWorkroomClick}
              loading={loading}
              isGenerating={isGenerating}
              kbUploading={kbUploading}
              onNewProject={openCreateProjectModal}
              onEditProject={openEditProjectModal}
              onNewWorkroom={openCreateWorkroom}
              isWorkroomRoute={isWorkroomRoute}
              workroomRefreshKey={workroomRefreshKey}
              onProjectsSectionOpenChange={setIsProjectsSectionOpen}
            />
          )}

          {shouldShowChatHistory && (
            <Box
              sx={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <ChatHistoryList />
            </Box>
          )}

          {isWorkroomRoute && (
            <Box
              sx={{
                width: "100%",
                flex: "1 1 0%",
                minHeight: isProjectsSectionOpen ? 140 : 0,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                pb: 1.5,
              }}
            >
              <Box
                sx={{
                  mt: "auto",
                  width: "100%",
                  minWidth: 0,
                  ...(isProjectsSectionOpen
                    ? {
                  minHeight: 0,
                  maxHeight: "100%",
                  flex: "0 1 auto",
                  display: "flex",
                  flexDirection: "column",
                      }
                    : { flexShrink: 0 }),
              }}
            >
              <WorkroomStrategyPanel
                projectId={workroomProjectId}
                clientId={
                  safeClient?.clientId ?? safeClient?.Id ?? safeClient?.id ?? null
                }
                refreshKey={workroomRefreshKey}
                  allowShrink={isProjectsSectionOpen}
                onEditStrategies={(workroom) => openEditWorkroom(workroom, 1)}
              />
              </Box>
            </Box>
          )}
        </Box>
  );

  const collapsedNav = (
    <Box
      sx={{
        position: "relative",
        width: SIDEBAR_COLLAPSED_WIDTH_PX,
        height: DESKTOP_SHELL_HEIGHT,
        minHeight: DESKTOP_SHELL_HEIGHT,
        boxSizing: "border-box",
        pt: `max(16px, ${SAFE_AREA_TOP})`,
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        flexDirection: "column",
        background:
          "linear-gradient(180deg, rgba(120,118,99,0.58) 0%, rgba(51,66,62,0.58) 42%, rgba(21,55,68,0.64) 100%)",
        backdropFilter: HOME_GLASS_BACKDROP,
        WebkitBackdropFilter: HOME_GLASS_BACKDROP,
        borderRight: HOME_GLASS_BORDER,
        boxShadow: "10px 0 28px rgba(0,0,0,0.16)",
      }}
    >
      <Tooltip title="Open sidebar" placement="right">
        <Box
          component="button"
          type="button"
          aria-label="Open sidebar"
          onClick={handleCollapsedLogoClick}
          disabled={isGenerating}
          sx={{
            width: 44,
            height: 44,
            p: 0,
            border: 0,
            borderRadius: "14px",
            background: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: isGenerating ? "not-allowed" : "pointer",
            opacity: isGenerating ? 0.65 : 1,
            transition: "background 180ms ease, opacity 180ms ease",
            "&:hover": {
              background: isGenerating ? "transparent" : "rgba(255,255,255,0.08)",
            },
          }}
        >
          <MenuIcon
            sx={{
              width: 26,
              height: 26,
              color: "#fff",
            }}
          />
        </Box>
      </Tooltip>

      <Box
        role="button"
        tabIndex={0}
        onClick={handleCollapsedBrandClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleCollapsedBrandClick();
          }
        }}
        sx={{
          position: "absolute",
          top: `max(10px, ${SAFE_AREA_TOP})`,
          left: SIDEBAR_COLLAPSED_WIDTH_PX + 16,
          width: 74,
          cursor: isGenerating ? "not-allowed" : "pointer",
          userSelect: "none",
          opacity: isGenerating ? 0.7 : 1,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="logo"
          width={74}
          height="auto"
          sx={{
            display: "block",
            maxWidth: 74,
            height: "auto",
          }}
        />
      </Box>
    </Box>
  );

  return (
    <>
      {isMobileNav && (
        <IconButton
          aria-label="Open navigation menu"
          onClick={() => setMobileDrawerOpen(true)}
          size="small"
          sx={{
            position: "fixed",
            top: `max(8px, calc(6px + ${SAFE_AREA_TOP}))`,
            left: "max(10px, env(safe-area-inset-left, 0px))",
            zIndex: 1300,
            color: "#fff",
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,255,255,0.14)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
          }}
        >
          <MenuIcon fontSize="medium" />
        </IconButton>
      )}

      {!isMobileNav && (
        isSidebarCollapsed ? collapsedNav : <Box sx={outerNavShellSx}>{navColumn}</Box>
      )}

      {isMobileNav && (
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: "min(300px, 88vw)",
              maxWidth: "100vw",
              boxSizing: "border-box",
              height: MOBILE_SHELL_HEIGHT,
              maxHeight: MOBILE_SHELL_HEIGHT,
              // Same glass treatment as the main hero / chrome so it matches the
              // desktop sidebar (transparent over the blurred Background).
              background: homeGlassHero.rest,
              backdropFilter: HOME_GLASS_BACKDROP,
              WebkitBackdropFilter: HOME_GLASS_BACKDROP,
              borderRight: HOME_GLASS_BORDER,
              pt: `max(12px, ${SAFE_AREA_TOP})`,
              pb: SAFE_AREA_BOTTOM,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
          }}
        >
          <Box
            sx={{
              ...outerNavShellSx,
              flex: 1,
              height: "auto",
              minHeight: 0,
              width: "100%",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              pt: 0,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {navColumn}
            </Box>
            <Box sx={{ flexShrink: 0, mt: "auto" }}>
              <Footer />
            </Box>
          </Box>
        </Drawer>
      )}

      <UploadKnowledgeBaseModal
        key={kbUploadModalKey}
        open={kbModalOpen}
        onClose={closeKbModal}
        loading={kbUploading}
        uploadProgress={kbUploadProgress}
        onUpload={handleKbModalUpload}
        forProjectUpload={kbUploadForProject}
      />

      <CreateClientModal
        open={clientModalOpen}
        onClose={closeClientModal}
        onSubmit={clientModalMode === "edit" ? handleUpdateClient : handleCreateClient}
        loading={loading}
        saveProgress={saveProgress}
        mode={clientModalMode}
        editTarget={clientEditTarget}
        defaultStep={clientDefaultStep}
      />

      <CreateProjectModal
        open={projectModalOpen}
        onClose={closeProjectModal}
        onSubmit={projectModalMode === "edit" ? updateProject : createProject}
        loading={loading}
        clientId={
          safeClient?.clientId ?? safeClient?.Id ?? safeClient?.id ?? null
        }
        mode={projectModalMode}
        editTarget={projectEditTarget}
        defaultStep={projectDefaultStep}
        onArchiveToggle={headerProjects.handleProjectArchiveToggle}
      />

      <CreateWorkroomModal
        open={workroomModalOpen}
        onClose={closeCreateWorkroom}
        onCreated={handleWorkroomSaved}
        onUpdated={handleWorkroomSaved}
        mode={workroomModalMode}
        editTarget={workroomEditTarget}
        defaultStep={workroomDefaultStep}
        projectsId={
          workroomModalMode === "edit"
            ? workroomProjectId
            : getProjectId(workroomModalProject)
        }
        projectName={
          workroomModalProject?.projectName ??
          workroomModalProject?.name ??
          workroomModalProject?.title ??
          ""
        }
      />

      <AppAlert
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        title="Coming Soon"
        message="The Projects management feature will be available in an upcoming release."
        isComingSoon
      />

      <AppAlert
        open={clientSwitchPromptOpen}
        onClose={() => setClientSwitchPromptOpen(false)}
        title="Client Selected"
        message="Would you like to continue in the current chat or start a new chat for this client?"
        onCancel={handleContinueCurrentChat}
        onConfirm={handleStartNewClientChat}
        cancelText="Continue Current Chat"
        confirmText="Start New Chat"
      />

      <Snackbar
        open={projectCreatedAlertOpen}
        autoHideDuration={2800}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setProjectCreatedAlertOpen(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setProjectCreatedAlertOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {projectCreatedAlertMessage || "Your project was created successfully."}
        </Alert>
      </Snackbar>
    </>
  );
}